import React, { useContext } from 'react';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatTextdirectionLToRIcon from '@mui/icons-material/FormatTextdirectionLToR';
import FormatTextdirectionRToLIcon from '@mui/icons-material/FormatTextdirectionRToL';
import { ColorPickerPopover } from '../ColorPickerPopover';
import { I18nReactContext } from '../../../../../../contexts/I18nContext';

/**
 * Typography section combining font size and text formatting controls
 * @param {object} props - Component props
 * @param {object} props.component - The component being edited
 * @param {function} props.onContentChange - Callback when content changes
 */
export const TypographySection = ({ component, onContentChange }) => {
    const { i18n } = useContext(I18nReactContext);

    const handleStyleChange = (styleProperty, value) => {
        const styleProps = {};
        if (styleProperty === 'fontSize') styleProps.fontSize = value;
        if (styleProperty === 'color') styleProps.color = value;
        onContentChange(component.id, undefined, styleProps);
    };

    return (
        <div className="menu-section typography-section">
            <h4>{i18n.translate('weezbeeCourse.editor.editorDrawer.typography', { defaultValue: 'Typography' })}</h4>

            {/* Row 1: Font Size + B/I/U buttons */}
            <div className="typography-row">
                <div className="border-radius-controls">
                    <div className="border-radius-input">
                        <input
                            type="number"
                            min="8"
                            max="72"
                            value={component.fontSize || 16}
                            onChange={(e) => {
                                const value = parseInt(e.target.value, 10);
                                if (!isNaN(value) && value >= 8 && value <= 72) {
                                    handleStyleChange('fontSize', value);
                                }
                            }}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                </div>

                <ToggleButtonGroup
                    value={[
                        component.bold ? 'bold' : null,
                        component.italic ? 'italic' : null,
                        component.underline ? 'underline' : null
                    ].filter(Boolean)}
                    onChange={(e, newFormats) => {
                        e.stopPropagation();
                        const isBold = newFormats.includes('bold');
                        const isItalic = newFormats.includes('italic');
                        const isUnderline = newFormats.includes('underline');
                        onContentChange(component.id, undefined, {
                            bold: isBold,
                            italic: isItalic,
                            underline: isUnderline
                        });
                    }}
                    aria-label="text formatting"
                    size="small"
                    className="format-toggle-group"
                >
                    <ToggleButton value="bold" aria-label="bold" title={i18n.translate('weezbeeCourse.editor.componentWrapper.boldButton')}>
                        <FormatBoldIcon fontSize="small" />
                    </ToggleButton>
                    <ToggleButton value="italic" aria-label="italic" title={i18n.translate('weezbeeCourse.editor.componentWrapper.italicButton')}>
                        <FormatItalicIcon fontSize="small" />
                    </ToggleButton>
                    <ToggleButton value="underline" aria-label="underline" title={i18n.translate('weezbeeCourse.editor.componentWrapper.underlineButton')}>
                        <FormatUnderlinedIcon fontSize="small" />
                    </ToggleButton>
                </ToggleButtonGroup>
            </div>

            {/* Row 2: Color picker + Direction */}
            <div className="typography-row">
                <ColorPickerPopover
                    color={component.color || '#000000'}
                    onChange={(color) => handleStyleChange('color', color)}
                    title={i18n.translate('weezbeeCourse.editor.editorDrawer.textColor', { defaultValue: 'Text Color' })}
                />

                <ToggleButtonGroup
                    value={component.direction || 'ltr'}
                    exclusive
                    onChange={(e, newDirection) => {
                        e.stopPropagation();
                        if (newDirection !== null) {
                            onContentChange(component.id, undefined, { direction: newDirection });
                        }
                    }}
                    aria-label="text direction"
                    size="small"
                    className="direction-toggle-group"
                >
                   
                    <ToggleButton value="rtl" aria-label="right to left" title={i18n.translate('weezbeeCourse.editor.component.rightToLeft')}>
                        <FormatTextdirectionRToLIcon fontSize="small" />
                    </ToggleButton>
                    <ToggleButton value="ltr" aria-label="left to right" title={i18n.translate('weezbeeCourse.editor.component.leftToRight')}>
                        <FormatTextdirectionLToRIcon fontSize="small" />
                    </ToggleButton>
                </ToggleButtonGroup>
            </div>
        </div>
    );
};
